import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { ContentOperationService } from "@/lib/api/content.operation.service";
import { useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import type { StudioContentStatus } from "@/lib/schema";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation } from "wouter";

interface UseContentLifecycleOptions {
  contentType: string;
  apiPath: string;
  id?: string;
  status?: StudioContentStatus;
  createdBy?: string | null;
  authorCoachId?: string | null;
  listRoute: string;
  editRoute?: (contentId: string, versionId?: string) => string;
  onSaveBeforeSubmit?: () => Promise<void>;
}

export function useContentLifecycle({
  contentType,
  apiPath,
  id,
  status,
  createdBy,
  authorCoachId,
  listRoute,
  editRoute,
  onSaveBeforeSubmit,
}: UseContentLifecycleOptions) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  const roles = user?.roles;
  const isCoach = roles?.includes("coach") || false;
  const isAdmin =
    roles?.includes("admin") || roles?.includes("super_admin") || false;
  const isOwner =
    !!user &&
    ((!!createdBy && createdBy === user.id) ||
      (!!authorCoachId && authorCoachId === user.id));

  const label = contentType.charAt(0).toUpperCase() + contentType.slice(1);

  const submitMutation = useMutation({
    mutationFn: async (targetId?: string) => {
      const resolvedId = targetId || id;
      if (!resolvedId) throw new Error("No content ID");
      if (onSaveBeforeSubmit) await onSaveBeforeSubmit();
      await api.post(`${apiPath}/${resolvedId}/submit`, {});
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: [apiPath] });
      queryClient.invalidateQueries({ queryKey: [apiPath] });
      if (id) queryClient.invalidateQueries({ queryKey: [apiPath, id] });
      toast({ title: "Submitted for approval" });
    },
    onError: (err: any) => {
      toast({
        title: err?.message || "Failed to submit",
        variant: "destructive",
      });
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: async (targetId?: string) => {
      const resolvedId = targetId || id;
      if (!resolvedId) throw new Error("No content ID");
      await api.post(`${apiPath}/${resolvedId}/withdraw`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [apiPath] });
      if (id) queryClient.invalidateQueries({ queryKey: [apiPath, id] });
      toast({ title: "Pulled back to draft" });
    },
    onError: (err: any) => {
      toast({
        title: err?.message || "Failed to withdraw",
        variant: "destructive",
      });
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (targetId?: string) => {
      const resolvedId = targetId || id;
      if (!resolvedId) throw new Error("No content ID");
      await ContentOperationService.reviewContent(contentType, resolvedId, {
        action: "approve",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [apiPath] });
      if (id) queryClient.invalidateQueries({ queryKey: [apiPath, id] });
      toast({ title: `${label} approved` });
    },
    onError: (err: any) => {
      toast({
        title: err?.message || "Failed to approve",
        variant: "destructive",
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({
      targetId,
      reviewNote,
    }: {
      targetId?: string;
      reviewNote: string;
    }) => {
      const resolvedId = targetId || id;
      if (!resolvedId) throw new Error("No content ID");
      await ContentOperationService.reviewContent(contentType, resolvedId, {
        action: "changes_requested",
        note: reviewNote,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [apiPath] });
      if (id) queryClient.invalidateQueries({ queryKey: [apiPath, id] });
      toast({ title: `${label} sent back with notes` });
    },
    onError: (err: any) => {
      toast({
        title: err?.message || "Failed to reject",
        variant: "destructive",
      });
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (targetId?: string) => {
      const resolvedId = targetId || id;
      if (!resolvedId) throw new Error("No content ID");
      await api.post(`${apiPath}/${resolvedId}/publish`, {});
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: [apiPath] });
      queryClient.invalidateQueries({ queryKey: [apiPath] });
      if (id) queryClient.invalidateQueries({ queryKey: [apiPath, id] });
      toast({ title: `${label} published and live` });
    },
    onError: (err: any) => {
      toast({
        title: err?.message || "Failed to publish",
        variant: "destructive",
      });
    },
  });

  // Set when the server refuses an archive because published content still
  // links to the item (409); the page asks before retrying with force.
  const [archiveConflict, setArchiveConflict] = useState<{
    id: string;
    message: string;
  } | null>(null);

  const archiveMutation = useMutation({
    mutationFn: async (target?: string | { id: string; force: true }) => {
      const resolvedId =
        (typeof target === "object" ? target.id : target) || id;
      if (!resolvedId) throw new Error("No content ID");
      const force = typeof target === "object" ? "?force=true" : "";
      await api.post(`${apiPath}/${resolvedId}/archive${force}`, {});
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: [apiPath] });
      queryClient.invalidateQueries({ queryKey: [apiPath] });
      if (id) queryClient.invalidateQueries({ queryKey: [apiPath, id] });
      toast({ title: `${label} archived` });
    },
    onError: (err: any, target) => {
      const resolvedId =
        (typeof target === "object" ? target.id : target) || id;
      if (err?.status === 409 && resolvedId) {
        setArchiveConflict({ id: resolvedId, message: err.message });
        return;
      }
      toast({
        title: err?.message || "Failed to archive",
        variant: "destructive",
      });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (targetId?: string) => {
      const resolvedId = targetId || id;
      if (!resolvedId) throw new Error("No content ID");
      await api.post(`${apiPath}/${resolvedId}/restore`, {});
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: [apiPath] });
      queryClient.invalidateQueries({ queryKey: [apiPath] });
      if (id) queryClient.invalidateQueries({ queryKey: [apiPath, id] });
      toast({ title: `${label} restored to draft` });
    },
    onError: (err: any) => {
      toast({
        title: err?.message || "Failed to restore",
        variant: "destructive",
      });
    },
  });

  const createVersionMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No content ID");
      return await api.post<{
        id: string;
        version: number;
        versionId?: string;
      }>(`${apiPath}/${id}/new-version`, {});
    },
    onSuccess: (data: { id: string; version: number; versionId?: string }) => {
      queryClient.invalidateQueries({ queryKey: [apiPath] });
      toast({ title: `New version (v${data.version}) created as draft` });
      if (editRoute) {
        setLocation(editRoute(data.id, data.versionId));
      }
    },
    onError: (err: any) => {
      toast({
        title: err?.message || "Failed to create new version",
        variant: "destructive",
      });
    },
  });

  const isLive = status === "published";
  const isArchived = status === "archived";
  const isRejected = status === "changes_requested";
  const isSubmitted = status === "submitted";
  // "published" is the approved state; no separate "approved" status exists.
  const isApproved = isLive;
  const isDraft = status === "draft";
  const isEditable = isDraft || isRejected;

  // Server only allows edits to draft or changes_requested versions
  // (validateTransition in server/src/common/validate-transition.ts).
  // A viewer may edit only if they are admin/super_admin, or a coach who owns
  // the content — and only while the content is in an editable stage. Everyone
  // else (non-owner coach, coach_manager, content_manager, influencer, …) is
  // read-only. Mirrors the server role gate + ownership check.
  const canEditRole = isAdmin || (isCoach && isOwner);
  const isReadOnly = !canEditRole || !isEditable;

  // Action availability (UI hints — server enforces)
  const canSubmit =
    (isDraft || isRejected) && (isAdmin || (isCoach && isOwner));
  const canWithdraw = isSubmitted && (isAdmin || (isCoach && isOwner));
  const canApprove = isSubmitted && isAdmin && !isOwner;
  const canReject = isSubmitted && isAdmin && !isOwner;
  // Approval directly publishes content (no separate "approved" state on server).
  // Keep canPublish false; approve flow handles transition submitted → published.
  const canPublish = false;
  const canArchive = isAdmin && isLive;
  const canRestore = isAdmin && isArchived;
  const canCreateVersion = isLive && (isAdmin || (isCoach && isOwner));
  const canDelete =
    (isCoach && isOwner && (isDraft || isRejected)) || (isAdmin && !isLive);

  return {
    submitMutation,
    withdrawMutation,
    approveMutation,
    rejectMutation,
    publishMutation,
    archiveMutation,
    archiveConflict,
    confirmArchive: () => {
      if (archiveConflict) {
        archiveMutation.mutate({ id: archiveConflict.id, force: true });
      }
      setArchiveConflict(null);
    },
    dismissArchiveConflict: () => setArchiveConflict(null),
    restoreMutation,
    createVersionMutation,
    isLive,
    isArchived,
    isRejected,
    isSubmitted,
    isApproved,
    isDraft,
    isReadOnly,
    isOwner,
    roles,
    canSubmit,
    canWithdraw,
    canApprove,
    canReject,
    canPublish,
    canArchive,
    canRestore,
    canCreateVersion,
    canDelete,
  };
}
